/**
 * Bandeja de solicitudes de baja de cuenta.
 *
 * El asociado pide la baja desde la app móvil (pantalla eliminar-cuenta) —
 * App Store (Guideline 5.1.1) exige que una app que crea cuentas permita
 * iniciar su eliminación in-app. Como Neocoop es una cooperativa regulada
 * (INGECOP) con capital del socio, la baja NO es instantánea: el equipo la
 * procesa manualmente desde acá.
 *
 * Mismo patrón que SolicitudesPage (estado de cuenta).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { formatDateTime, formatRelative } from '@/lib/format'

// ---- tipos ----
type Estado = 'pendiente' | 'en_proceso' | 'procesada' | 'cancelada'

type BajaRow = {
  id: string
  asociado_id: string
  motivo: string | null
  estado: Estado
  requested_at: string
  attended_at: string | null
  notas_admin: string | null
  // joined
  asociado?: {
    no_asociado: string
    nombre_completo: string
  } | null
}

const ESTADO_LABEL: Record<Estado, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En proceso',
  procesada: 'Procesada',
  cancelada: 'Cancelada',
}

// solicitud_baja usa 'procesada'/'cancelada'; reusamos clases de badge
// existentes para no agregar CSS.
const ESTADO_BADGE: Record<Estado, string> = {
  pendiente: 'badge-pendiente',
  en_proceso: 'badge-en_proceso',
  procesada: 'badge-success',
  cancelada: 'badge-muted',
}

const FILTERS: { id: Estado | 'all'; label: string }[] = [
  { id: 'pendiente', label: 'Pendientes' },
  { id: 'en_proceso', label: 'En proceso' },
  { id: 'procesada', label: 'Procesadas' },
  { id: 'all', label: 'Todas' },
]

export function BajasPage() {
  const [rows, setRows] = useState<BajaRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<Estado | 'all'>('pendiente')
  const [pendingId, setPendingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Traemos TODAS las solicitudes (límite 200) y filtramos en cliente —
      // así los contadores de los chips son correctos y el cambio de filtro
      // es instantáneo sin round-trip.
      const { data, error: err } = await supabase
        .from('solicitud_baja')
        .select(
          'id, asociado_id, motivo, estado, requested_at, attended_at, ' +
            'notas_admin, asociado:asociado_id ( no_asociado, nombre_completo )',
        )
        .order('requested_at', { ascending: false })
        .limit(200)
      if (err) throw err
      setRows((data ?? []) as unknown as BajaRow[])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const visibleRows = useMemo(() => {
    if (filter === 'all') return rows
    return rows.filter((r) => r.estado === filter)
  }, [rows, filter])

  const setEstado = async (id: string, nextEstado: Estado) => {
    setPendingId(id)
    setError(null)
    try {
      const patch: Record<string, unknown> = { estado: nextEstado }
      if (nextEstado === 'procesada' || nextEstado === 'cancelada') {
        patch.attended_at = new Date().toISOString()
        const { data: u } = await supabase.auth.getUser()
        if (u.user?.id) patch.attended_by = u.user.id
      }
      const { error: err } = await supabase
        .from('solicitud_baja')
        .update(patch)
        .eq('id', id)
      if (err) throw err
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error desconocido.')
    } finally {
      setPendingId(null)
    }
  }

  const counts = useMemo(() => {
    const base: Record<string, number> = {
      pendiente: 0,
      en_proceso: 0,
      procesada: 0,
      cancelada: 0,
    }
    for (const r of rows) base[r.estado] = (base[r.estado] ?? 0) + 1
    return base
  }, [rows])

  return (
    <>
      <div className="filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            className={'chip' + (filter === f.id ? ' active' : '')}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            {f.id !== 'all' && counts[f.id] !== undefined ? (
              <span style={{ opacity: 0.7, fontVariant: 'tabular-nums' }}>
                · {counts[f.id]}
              </span>
            ) : null}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-ghost"
          onClick={load}
          disabled={loading}
          style={{ padding: '6px 14px' }}
        >
          {loading ? 'Recargando…' : 'Recargar'}
        </button>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Solicitada</th>
              <th>Asociado</th>
              <th>Motivo</th>
              <th>Estado</th>
              <th className="actions">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="empty">Cargando…</div>
                </td>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="empty">
                    <div className="empty-title">Sin solicitudes de baja</div>
                    <div className="empty-sub">
                      No hay registros para el filtro seleccionado.
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              visibleRows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {formatRelative(r.requested_at)}
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      {formatDateTime(r.requested_at)}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {r.asociado?.nombre_completo ?? '—'}
                    </div>
                    <div className="mono muted" style={{ fontSize: 12 }}>
                      {r.asociado?.no_asociado ?? '—'}
                    </div>
                  </td>
                  <td style={{ maxWidth: 320 }}>
                    {r.motivo ? (
                      <span style={{ fontSize: 13 }}>{r.motivo}</span>
                    ) : (
                      <span className="muted" style={{ fontSize: 12 }}>
                        Sin motivo
                      </span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${ESTADO_BADGE[r.estado]}`}>
                      {ESTADO_LABEL[r.estado] ?? r.estado}
                    </span>
                    {r.attended_at ? (
                      <div
                        className="muted"
                        style={{ fontSize: 11, marginTop: 4 }}
                      >
                        {formatRelative(r.attended_at)}
                      </div>
                    ) : null}
                  </td>
                  <td className="actions">
                    <RowActions
                      estado={r.estado}
                      pending={pendingId === r.id}
                      onTomar={() => setEstado(r.id, 'en_proceso')}
                      onMarcarProcesada={() => setEstado(r.id, 'procesada')}
                      onCancelar={() => setEstado(r.id, 'cancelada')}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

type RowActionsProps = {
  estado: Estado
  pending: boolean
  onTomar: () => void
  onMarcarProcesada: () => void
  onCancelar: () => void
}

function RowActions({
  estado,
  pending,
  onTomar,
  onMarcarProcesada,
  onCancelar,
}: RowActionsProps) {
  if (pending) return <span className="muted">…</span>

  if (estado === 'pendiente') {
    return (
      <div style={{ display: 'inline-flex', gap: 6 }}>
        <button className="btn btn-ghost" onClick={onTomar}>
          Tomar
        </button>
        <button className="btn btn-primary" onClick={onMarcarProcesada}>
          Marcar procesada
        </button>
      </div>
    )
  }
  if (estado === 'en_proceso') {
    return (
      <div style={{ display: 'inline-flex', gap: 6 }}>
        <button className="btn btn-danger" onClick={onCancelar}>
          Cancelar
        </button>
        <button className="btn btn-primary" onClick={onMarcarProcesada}>
          Marcar procesada
        </button>
      </div>
    )
  }
  return (
    <span className="muted" style={{ fontSize: 12 }}>
      —
    </span>
  )
}
